import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';

export interface MultiSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps<TFieldValues extends FieldValues = FieldValues> {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  options: MultiSelectOption[];
  displayEmpty?: boolean;
  emptyLabel?: string;
  renderValue?: (value: unknown) => React.ReactNode;
}

export function MultiSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  disabled,
  required,
  helperText,
  options,
  displayEmpty,
  emptyLabel = 'Select options',
  renderValue,
  ...selectProps
}: MultiSelectProps<TFieldValues> & Omit<MuiSelectProps, 'name' | 'control' | 'error' | 'helperText' | 'label' | 'displayEmpty' | 'multiple' | 'renderValue'>) {
  const labelId = `${name}-label`;

  const defaultRenderValue = (value: unknown) => {
    const selected = value as (string | number)[];
    if (selected.length === 0) {
      return <em>{emptyLabel}</em>;
    }
    const selectedLabels = selected.map(value => {
      const option = options.find(opt => opt.value === value);
      return option?.label || String(value);
    });
    return selectedLabels.join(', ');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth error={!!fieldState.error}>
          {label && (
            <InputLabel id={labelId}>
              {label}
              {required && ' *'}
            </InputLabel>
          )}
          <MuiSelect
            {...field}
            {...selectProps}
            labelId={labelId}
            label={label}
            disabled={disabled}
            displayEmpty={displayEmpty}
            multiple
            renderValue={renderValue || defaultRenderValue}
          >
            {displayEmpty && (
              <MenuItem value="">
                <em>{emptyLabel}</em>
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                <Checkbox checked={field.value?.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </MuiSelect>
          <FormHelperText>{fieldState.error?.message || helperText}</FormHelperText>
        </FormControl>
      )}
    />
  );
}